'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import IconComponent from 'ui/src/components/Icon';
import twMerge from 'utilities/src/twMerge';
import usePostsSearch from 'hooks/usePostsSearch';
import Image from '../Image';
import ButtonLink from '../ButtonLink';
import type { NavbarData } from './types';

const SEARCH_DEBOUNCE_MS = 400;
const MAX_ARTICLE_MATCHES = 3;

export const getSearchPath = (query: string) => `/search?value=${encodeURIComponent(query)}`;

type Props = Pick<NavbarData, 'popularSearches' | 'recentlyUpdated'> & {
  id: string;
  isOpen: boolean;
  query: string;
  onNavigate: () => void;
  className?: string;
};

function SectionTitle({ children, className }: { children: React.ReactNode; className: string }) {
  return <p className={twMerge('site-overline-s', className)}>{children}</p>;
}

function TopicMatch({
  href,
  title,
  description,
  onNavigate,
}: {
  href: string;
  title: string;
  description: string;
  onNavigate: () => void;
}) {
  return (
    <Link
      href={href}
      prefetch={false}
      onClick={onNavigate}
      className="bg-site-neutrals-25 border-site-neutrals-50 hover:border-site-neutrals-200 text-site-neutrals-600 hover:text-site-neutrals-900 group flex items-center justify-between gap-2 rounded border-2 px-3 py-2.5"
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="site-paragraph-m text-site-neutrals-900 font-bold">{title}</span>
        <span className="site-paragraph-s text-site-neutrals-600">{description}</span>
      </span>
      <IconComponent iconName="ArrowForward" size="IconSM" className="bg-current" />
    </Link>
  );
}

export default function SearchDropdown({
  id,
  isOpen,
  query,
  popularSearches,
  recentlyUpdated,
  onNavigate,
  className,
}: Props) {
  const trimmedQuery = query.trim();
  const [searchTerm, setSearchTerm] = useState(trimmedQuery);

  useEffect(() => {
    const timeout = setTimeout(() => setSearchTerm(trimmedQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [trimmedQuery]);

  const { posts, total, categories } = usePostsSearch(searchTerm, 1, {
    itemsPerPage: MAX_ARTICLE_MATCHES,
  });
  const topicMatches = categories.map(({ name, url, description }) => ({
    href: url,
    title: name,
    description,
  }));

  // The no-results state isn't designed yet, so a query without matches shows no dropdown.
  if (trimmedQuery && !topicMatches.length && !posts.length) return null;

  return (
    <div
      id={id}
      tabIndex={-1}
      className={twMerge(
        'bg-site-neutrals-0 border-site-neutrals-200 shadow-lv5 absolute top-full flex flex-col gap-8 rounded border p-5 outline-none transition-all duration-200',
        isOpen ? 'visible translate-y-0 opacity-100' : 'pointer-events-none invisible -translate-y-2 opacity-0',
        className,
      )}
    >
      {!trimmedQuery ? (
        <>
          <div className="flex flex-col gap-4">
            <SectionTitle className="text-site-neutrals-400">Popular searches</SectionTitle>
            <ul className="flex flex-col gap-2">
              {popularSearches.map((popularSearch) => (
                <li key={popularSearch}>
                  <Link
                    href={getSearchPath(popularSearch)}
                    prefetch={false}
                    onClick={onNavigate}
                    className="site-paragraph-m text-site-neutrals-900 flex items-center gap-2.5 py-1.5 font-medium hover:underline"
                  >
                    <IconComponent
                      iconName="ArrowForward"
                      size="IconS"
                      className="bg-site-neutrals-200 -rotate-45"
                    />
                    {popularSearch}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {recentlyUpdated.length > 0 && (
            <div className="flex flex-col gap-4">
              <SectionTitle className="text-site-neutrals-400">Recently updated</SectionTitle>
              <ul className="flex flex-col gap-2">
                {recentlyUpdated.map((subcategory) => (
                  <li key={subcategory.description}>
                    <TopicMatch
                      href={subcategory.href}
                      title={subcategory.subcategoryName}
                      description={subcategory.description}
                      onNavigate={onNavigate}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <>
          {topicMatches.length > 0 && (
            <div className="flex flex-col gap-4">
              <SectionTitle className="text-site-neutrals-600">Categories</SectionTitle>
              <ul className="flex flex-col gap-2">
                {topicMatches.map((topic) => (
                  <li key={topic.href}>
                    <TopicMatch {...topic} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {posts.length > 0 && (
            <div className="flex flex-col gap-4">
              <SectionTitle className="text-site-neutrals-600">Articles</SectionTitle>
              <ul className="flex flex-col gap-2">
                {posts.map(
                  (post) =>
                    post && (
                      <li key={post.id}>
                        <Link
                          href={`/${post.slug}/`}
                          prefetch={false}
                          onClick={onNavigate}
                          className="mdS:items-center mdS:gap-3 group flex items-start gap-2 py-1.5"
                        >
                          <span className="mdS:mt-0 mdS:h-16 mdS:w-[7.125rem] bg-site-neutrals-25 relative mt-1 h-8 w-[3.5625rem] shrink-0 overflow-hidden">
                            {post.featuredImage.file && (
                              <Image
                                src={post.featuredImage.file}
                                alt=""
                                role="presentation"
                                fill
                                sizes="(min-width: 640px) 7.125rem, 3.5625rem"
                                className="object-cover"
                              />
                            )}
                          </span>
                          <span className="flex min-w-0 flex-col gap-0.5">
                            <span className="site-paragraph-s mdS:site-paragraph-m text-site-neutrals-900 font-medium group-hover:underline">
                              {post.title}
                            </span>
                            <span className="site-paragraph-xs mdS:site-paragraph-s text-site-neutrals-600">
                              {[post.category.name, post.timeToRead && `${post.timeToRead} min read`]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ),
                )}
              </ul>
            </div>
          )}
          {!!total && (
            <div className="border-site-neutrals-200 border-t pb-1 pt-3">
              <ButtonLink
                href={getSearchPath(searchTerm)}
                prefetch={false}
                onClick={onNavigate}
                variant="TextOnly"
                variantColor="Black"
                size="Small"
                iconName="ArrowForward"
                iconSize="IconS"
              >
                See all {total} results for &quot;{searchTerm}&quot;
              </ButtonLink>
            </div>
          )}
        </>
      )}
    </div>
  );
}
